// src/utils/appLayout.utils.ts
import type { NotificationItem, User } from '@/types/UserState';

export interface HeaderNotification {
  id: string;
  source: string;
  time: string;
  content: string;
  read: boolean;
  onSelect?: () => void;
}

export interface UserProfile {
  name: string;
  email: string;
  phone: string;
  avatarUrl?: string;
}

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;

export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (MS_PER_SECOND * SECONDS_PER_MINUTE));
  const diffHours = Math.floor(
    diffMs / (MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR),
  );
  const diffDays = Math.floor(
    diffMs / (MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY),
  );

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < SECONDS_PER_MINUTE) return `${String(diffMinutes)}m ago`;
  if (diffHours < HOURS_PER_DAY) return `${String(diffHours)}h ago`;
  if (diffDays < DAYS_PER_WEEK) return `${String(diffDays)}d ago`;
  return date.toLocaleDateString();
}

export function createHeaderNotification(
  item: NotificationItem,
  onMarkRead: (notificationId: string) => void,
): HeaderNotification {
  const content =
    item.target !== undefined ? `${item.action} ${item.target}` : item.action;

  return {
    id: item.id,
    source: item.six.name,
    time: formatRelativeTime(item.createdAt),
    content: `${item.actor.name} ${content}`,
    read: item.read,
    onSelect: () => {
      if (!item.read) {
        onMarkRead(item.id);
      }
    },
  };
}

export function resolveUserName(user: User | null): string {
  if (user === null) return 'User';
  const nameCandidate = user.name.trim();
  return nameCandidate.length > 0 ? nameCandidate : 'User';
}

export function resolveUserEmail(user: User | null): string | undefined {
  if (user === null) return undefined;
  return typeof user.email === 'string' && user.email.length > 0 ? user.email : undefined;
}

export function resolveOrganizationName(user: User | null): string {
  if (user === null) return 'Organization';

  const organizationCandidate = user.organization.trim();
  return organizationCandidate.length > 0 ? organizationCandidate : 'Organization';
}

export function resolveUserPhone(user: User | null): string {
  if (user === null) return '';
  return user.phone.length > 0 ? user.phone : '';
}

export function buildUserProfile(user: User | null): UserProfile {
  return {
    name: resolveUserName(user),
    email: resolveUserEmail(user) ?? '',
    phone: resolveUserPhone(user),
    avatarUrl: undefined,
  };
}
